// @ts-nocheck
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";

import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import debounce from "lodash.debounce";
import { matchSorter } from "match-sorter";
import styled from "styled-components";

import { MarkdownLite, Stack, Text } from "@origin-digital/ods-core";
import { Autocomplete } from "@origin-digital/ods-lab";

import { content } from "./content";
import { isManualEntryOption, isQuerySearchable } from "./helpers";
import {
  AddressManualEntry,
  AddressManualEntryProps,
  AddressManualEntryValues,
} from "../AddressManualEntry";
import { call, createSafeLabel, getOptionLabel } from "../helpers";
import { AddressResult } from "../models";

const StyledAutocomplete = styled(Autocomplete)`
  & div[data-id="spinner"] {
    margin-top: -19px;
  }
`;

export interface AddressLookupUncontrolledProps {
  id: string;
  fetchAddresses: (query: string) => Promise<AddressResult[]>;
  "data-id"?: string;
  disabled?: boolean;
  label?: string;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  value?: string;
  endIcon?: "success" | "error" | "validating" | ReactNode;
  forcePopupIcon?: boolean;
  disableClearable?: boolean;
  onSelect?: (selectedOption: AddressResult) => Promise<void>;
  onError?: (error: Error) => void;
  onQueryChange?: (value: string) => void;
  onBlur?: (event: any) => void;
  manualEntry?: boolean;
  manualEntryProps?: AddressManualEntryProps;
  manualEntryValues?: AddressManualEntryValues;
  onManualEntryShown?: () => Promise<void>;
  isLoading?: boolean;
}
export type AddressLookupControlledProps = AddressLookupUncontrolledProps &
  Required<Pick<AddressLookupUncontrolledProps, "value" | "onQueryChange">>;

export type AddressLookupProps =
  | AddressLookupUncontrolledProps
  | AddressLookupControlledProps;

export const AddressLookup = ({
  id,
  disabled,
  placeholder,
  helperText,
  label,
  error,
  value,
  manualEntry,
  manualEntryProps,
  manualEntryValues,
  onManualEntryShown,
  endIcon,
  forcePopupIcon,
  disableClearable,
  isLoading,
  onBlur,
  fetchAddresses,
  onSelect,
  onError,
  onQueryChange,
  ...props
}: AddressLookupProps) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(
    !!manualEntryValues?.postcode || false
  );
  const [loading, setLoading] = useState(true);

  const isControlled = value !== undefined;
  const dataId = props["data-id"] || id;

  const setManualOption = () => {
    if (manualEntry) {
      setOptions([content.manualEntry]);
    }
  };

  useEffect(() => {
    setLoading(isLoading ?? false);
  }, [isLoading]);

  const handleDelayedFetchOptions = useCallback(
    debounce(async (newInputValue) => {
      setLoading(true);

      try {
        const result = await fetchAddresses(newInputValue);

        if (result.length > 0) {
          setOptions(result);
        } else {
          setManualOption();
        }
      } catch (delayedFetchError) {
        call(onError, delayedFetchError);
        setManualOption();
      }

      setLoading(false);
    }, 400),
    []
  );

  useEffect(() => {
    // setShowManualEntry(false);
    // clear pre-loaded options when value is cleared
    if (value === undefined || value === null || value === "") {
      setOptions([]);
    }

    (async () => {
      // needs at least 3 characters
      if (isControlled && value !== "" && value.length > 3) {
        await handleDelayedFetchOptions(value);
      }
    })();
  }, [value]);

  const filterOptions = (
    // eslint-disable-next-line @typescript-eslint/no-shadow
    options: AddressResult[] | string[],
    params
  ): AddressResult[] => {
    if (options && options.length === 1 && options[0] === content.manualEntry) {
      return [
        {
          addressId: "",
          label: options[0],
          postcode: "",
        },
      ];
    }
    return matchSorter<AddressResult>(
      options as AddressResult[],
      params.inputValue,
      { keys: ["label"], threshold: matchSorter.rankings.NO_MATCH }
    );
  };

  const onChange = async (_, newValue) => {
    if (newValue) {
      if (isManualEntryOption(newValue)) {
        setShowManualEntry(true);

        if (onManualEntryShown) {
          await onManualEntryShown();
        }

        return;
      }

      call(onSelect, newValue);
    }

    setInputValue(getOptionLabel(newValue));
  };

  const onInputChange = async (
    _: ChangeEvent<HTMLInputElement>,
    newInputValue,
    reason
  ) => {
    if (reason === "reset") {
      return;
    }

    call(onQueryChange, newInputValue);

    setInputValue(newInputValue);
    setShowManualEntry(false);

    if (!isQuerySearchable(newInputValue)) {
      return;
    }

    await handleDelayedFetchOptions(newInputValue);
  };

  const renderOption = (optionProps, option: AddressResult) => {
    const { label: optionLabel } = option;

    if (isManualEntryOption(option)) {
      return (
        <Text {...optionProps} tone="neutralDark">
          {optionLabel}
        </Text>
      );
    }

    const matches = match(optionLabel, inputValue);
    const parts = parse(optionLabel, matches);

    return (
      <Text {...optionProps}>
        <MarkdownLite>
          {parts
            .map(({ highlight, text }) => (highlight ? `**${text}**` : text))
            .join("")}
        </MarkdownLite>
      </Text>
    );
  };

  const muiProps = {
    renderOption,
    loading,
    onChange,
    onInputChange,
    filterOptions,
    getOptionLabel,
    inputValue: isControlled ? value : inputValue,
    value: {
      label: isControlled ? value : inputValue,
    },
  };

  const manualProps = {
    ...manualEntryProps,
    id: `${id}-manual-entry`,
    "data-id": `${dataId}-manual-entry`,
  };

  return (
    <Stack data-id={dataId}>
      <StyledAutocomplete
        id={id}
        data-id={`${dataId}-autocomplete`}
        options={options}
        muiProps={muiProps}
        error={error}
        placeholder={createSafeLabel(placeholder)}
        label={createSafeLabel(label)}
        helperText={helperText}
        disabled={disabled}
        loading={loading}
        endIcon={endIcon}
        forcePopupIcon={forcePopupIcon}
        disableClearable={disableClearable}
        onBlur={onBlur}
        {...muiProps}
      />

      {showManualEntry && <AddressManualEntry {...manualProps} />}
    </Stack>
  );
};

AddressLookup.defaultProps = {
  label: content.label,
  helperText: content.helperText,
  placeholder: content.placeholder,
};
